import { useEffect, useState } from 'react';

import {
  OrdainedResponseModel,
  updateOrdainedPCO,
  UpdateOrdainedPCOPayload,
  uploadOrdainedFile,
} from '../../api/ordained';
import { Modal } from '../../components/v2/Modal/Modal';
import { getAPIErrorMessage } from '../../helpers/helper';
import { useDashboard } from '../../provider/dashboardProvider';
import { usePCO } from '../../provider/pcoProvider';
import { useSnackbar } from '../../provider/snackbarProvider';

import PCOForm, { DocumentSupportType, PCOFormValues } from './PCOForm';
import PCOLockedSummary from './PCOLockedSummary';

// Extract a display filename from an upload URL, stripping the UUID prefix.
const filenameFromUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  let pathname: string;
  try {
    pathname = new URL(url).pathname;
  } catch {
    return null;
  }
  const tail = pathname.split('/').pop() || '';
  const decoded = (() => {
    try {
      return decodeURIComponent(tail);
    } catch {
      return tail;
    }
  })();
  const dashIdx = decoded.indexOf('-', 36);
  return dashIdx > 0 ? decoded.slice(dashIdx + 1) : decoded || null;
};

const buildInitialValues = (
  ordained: OrdainedResponseModel | null | undefined
): PCOFormValues => ({
  firstName: ordained?.first_name ?? '',
  middleName: ordained?.middle_name ?? '',
  lastName: ordained?.last_name ?? '',
  shippingAddress: ordained?.shipping_address ?? '',
  shippingAddress2: ordained?.shipping_address2 ?? '',
  shippingCity: ordained?.shipping_city ?? '',
  shippingState: ordained?.shipping_state ?? '',
  shippingZip: ordained?.shipping_zip ?? '',
  shippingCountry: ordained?.shipping_country ?? 'United States',
  documentSupportTypes:
    (ordained?.documentSupportTypes as DocumentSupportType[]) ?? [],
  documentUploadUrl: ordained?.document_upload_url ?? null,
  documentUploadFile: null,
  uploadedFilename: filenameFromUrl(ordained?.document_upload_url),
});

const PCOApplicationModal = () => {
  const {
    pcoApplicationModalOpen,
    setPcoApplicationModalOpen,
    setPcoSuccessModalOpen,
  } = usePCO();

  const { ordained, fetchOrdained } = useDashboard();
  const { openSnackBar } = useSnackbar();

  const [submitting, setSubmitting] = useState(false);

  // Refetch ordained on open so we prefill with the latest data.
  useEffect(() => {
    if (pcoApplicationModalOpen) {
      void fetchOrdained();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcoApplicationModalOpen]);

  const editable = ordained?.editable !== false;

  const handleSubmit = async (
    values: PCOFormValues,
    initialValues: PCOFormValues
  ) => {
    if (submitting) return;
    setSubmitting(true);

    try {
      let documentUploadUrl = values.documentUploadUrl;
      if (values.documentUploadFile) {
        const uploadResp = await uploadOrdainedFile(values.documentUploadFile);
        if (!uploadResp.success || !uploadResp.data?.url) {
          openSnackBar(uploadResp.message || 'File upload failed', 'error');
          return;
        }
        documentUploadUrl = uploadResp.data.url;
      }

      // Only include name fields when edited.
      const payload: UpdateOrdainedPCOPayload = {
        shippingAddress: values.shippingAddress,
        shippingAddress2: values.shippingAddress2,
        shippingCity: values.shippingCity,
        shippingState: values.shippingState,
        shippingZip: values.shippingZip,
        shippingCountry: values.shippingCountry,
        documentSupportTypes: values.documentSupportTypes,
        documentUploadUrl,
      };
      if (values.firstName !== initialValues.firstName) {
        payload.firstName = values.firstName;
      }
      if (values.middleName !== initialValues.middleName) {
        payload.middleName = values.middleName;
      }
      if (values.lastName !== initialValues.lastName) {
        payload.lastName = values.lastName;
      }

      const response = await updateOrdainedPCO(payload);
      if (response.success) {
        void fetchOrdained();
        setPcoApplicationModalOpen(false);
        setPcoSuccessModalOpen(true);
      } else {
        openSnackBar(response.message || 'Something went wrong', 'error');
      }
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      openSnackBar(getAPIErrorMessage(err as any), 'error');
      // 409 = already printed; refresh so the UI locks.
      void fetchOrdained();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={pcoApplicationModalOpen}
      onClose={() => setPcoApplicationModalOpen(false)}
    >
      {editable ? (
        <PCOForm
          initialValues={buildInitialValues(ordained)}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      ) : (
        <PCOLockedSummary
          status={ordained?.status}
          onClose={() => setPcoApplicationModalOpen(false)}
        />
      )}
    </Modal>
  );
};

export default PCOApplicationModal;
